/* GFI Nexus — Resend send helper
   ─────────────────────────────────────────────────────────────────
   Frontend wrapper that POSTs the email payload to our Netlify
   Function /send-email, which holds the secret RESEND_API_KEY
   server-side and forwards to Resend.

   Public API matches gmailApi.sendEmail for drop-in compatibility.
   Differences from the Gmail version:
     - opts.from MUST be an @gfinexus.com address (Resend only
       sends from verified domains)
     - opts.replyTo is now critical — recipients will reply to this
       address, not to the masked @gfinexus.com sender
     - No OAuth, no popups, no consent screens
*/

const SEND_FN_URL = "/.netlify/functions/send-email";

/* Blob → base64 (NOT url-safe). Resend expects standard base64. */
function blobToBase64(blob) {
  return new Promise(function (resolve, reject) {
    const r = new FileReader();
    r.onload = function () {
      const s = r.result || "";
      const comma = s.indexOf(",");
      resolve(comma >= 0 ? s.substring(comma + 1) : s);
    };
    r.onerror = function () { reject(r.error || new Error("blob → base64 failed")); };
    r.readAsDataURL(blob);
  });
}

/* Send an email via Resend (through our Netlify Function).
   opts:
     - to        string[]   recipient emails
     - cc        string[]   optional CC
     - bcc       string[]   optional BCC
     - subject   string
     - text      string     plain-text body
     - html      string     HTML body (preferred)
     - from      string     masked From address (must be @gfinexus.com)
     - replyTo   string     real reply-to address (where responses go)
     - attachments [{ filename, mimeType, blob }]

   Returns { id } on success, throws on error. */
export async function sendEmail(opts) {
  if (!opts || !opts.to || opts.to.length === 0) {
    throw new Error("sendEmail: at least one recipient (to) is required");
  }
  if (!opts.from) {
    throw new Error("sendEmail: from is required and must be an @gfinexus.com address");
  }
  if (!opts.subject) throw new Error("sendEmail: subject is required");
  if (!opts.html && !opts.text) throw new Error("sendEmail: html or text body is required");

  /* Serialize attachments — Resend expects base64 content. */
  const atts = opts.attachments || [];
  /* Netlify Functions (this one included — AWS Lambda/API Gateway
     under the hood) reject a synchronous invocation's request body
     over ~6MB, BEFORE our function code (or its nice {success,error}
     JSON shape) ever runs — the platform's own generic rejection has
     no `.error` field, so callers saw a bare "Send failed: HTTP 400"
     with no indication of why (2026-09-14, Arturo: real send with a
     scanned inspection-report attachment). Base64 inflates raw bytes
     by ~4/3, so a 4MB combined attachment budget stays safely under
     that ceiling once JSON/header overhead is added too. Checked
     client-side, before ever building the request, so the failure is
     immediate and actionable instead of a round-trip to a platform
     error page. */
  const MAX_ATTACHMENTS_BYTES = 4 * 1024 * 1024;
  const totalBytes = atts.reduce(function (s, a) { return s + ((a.blob && a.blob.size) || 0); }, 0);
  if (totalBytes > MAX_ATTACHMENTS_BYTES) {
    const mb = (totalBytes / (1024 * 1024)).toFixed(1);
    throw new Error("sendEmail: attachments too large (" + mb + " MB) — the email server rejects sends over ~4 MB of combined attachments. Compress the file(s) or split them across multiple emails.");
  }
  const serialized = [];
  for (const a of atts) {
    const content = await blobToBase64(a.blob);
    serialized.push({
      filename: a.filename,
      content: content,
    });
  }

  const payload = {
    from: opts.from,
    to: opts.to,
    cc: opts.cc || [],
    bcc: opts.bcc || [],
    replyTo: opts.replyTo || "",
    subject: opts.subject,
    html: opts.html || null,
    text: opts.text || null,
    attachments: serialized,
  };

  const res = await fetch(SEND_FN_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(function () { return {}; });
  if (!res.ok) {
    throw new Error("Send failed: " + (data.error || ("HTTP " + res.status)));
  }
  return data;
}

/* Quick validation: returns true for a syntactically plausible email. */
export function isValidEmail(s) {
  if (typeof s !== "string") return false;
  const trimmed = s.trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}
